/**
 * AdminTable — B 端後台資料表（排序 / 前端分頁 / 狀態篩選）
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init；分頁項目樣板共用 pagination.js（與前台逐字一致）。
 *
 * 設計對齊 render-cards.js：escapeHtml + WeakMap 實例快取 + data-* 宣告式 auto-init。
 * 本檔在無 [data-admin-table] DOM 時 no-op（不 throw），供 Wave3 列表頁接用。
 *
 * 宣告式（auto-init via [data-admin-table]）：
 *   <div class="data-table-wrapper">
 *     <table class="data-table data-table--hover" data-admin-table
 *            data-page-size="10">
 *       <thead>
 *         <tr>
 *           <th class="data-table__header data-table__header--sortable" data-sort-key="id" aria-sort="none">編號</th>
 *           <th class="data-table__header data-table__header--sortable" data-sort-key="date" data-sort-type="date" aria-sort="none">送審日</th>
 *           <th class="data-table__header">狀態</th>
 *         </tr>
 *       </thead>
 *       <tbody>
 *         <tr data-status="pending">
 *           <td class="data-table__cell--mono" data-label="編號" data-sort-value="A-001">A-001</td>
 *           <td data-label="送審日" data-sort-value="2026-06-01">2026.06.01</td>
 *           <td data-label="狀態"><span class="badge badge--warning">待審</span></td>
 *         </tr>
 *       </tbody>
 *     </table>
 *   </div>
 *
 * 篩選（外部 toggle 群，role=group + aria-pressed；非 tab，無 tabpanel）：
 *   <div class="tabs tabs--underline" role="group" aria-label="狀態篩選" data-admin-table-filter="#consult-table">
 *     <button class="tabs__item is-active" aria-pressed="true" data-filter-value="all">全部</button>
 *     <button class="tabs__item" aria-pressed="false" data-filter-value="pending">待審</button>
 *   </div>
 *   （選取 keys off data-filter-value / .is-active，不依賴 role；列以 tr[data-status] 比對；all 不過濾。
 *    點擊時同步 .is-active + aria-pressed，過濾邏輯只看 data-filter-value）
 *
 * 分頁：容器若含 .pagination[data-admin-table-pager] 則由本類接管渲染。
 *
 * 卡片化（≤768）：
 *   排序 / 分頁 / 篩選全在 <tr> DOM 層運作，與 CSS 是 table 還是 cards 顯示無關，
 *   故卡片化不破壞三功能（不依賴 column index 的視覺位置，只依 data-sort-value）。
 *   reorder 模式在卡片化下 drag 仍可用，另額外渲染上移 / 下移鈕作鍵盤 / 觸控降級。
 *
 * 排序模式拖曳重排（categories 用，opt-in via [data-admin-table-reorder]）：
 *   <table class="data-table" data-admin-table data-admin-table-reorder>
 *     <tbody>
 *       <tr data-row-id="cat-1">
 *         <td><span class="data-table__reorder-grip" aria-hidden="true">⋮⋮</span></td>
 *         <td>除皺</td>
 *       </tr>
 *     </tbody>
 *   </table>
 *   - 拖曳：抓 .data-table__reorder-grip（無 grip 則整列），pointer 事件（滑鼠 + 觸控通用）
 *   - 降級：每列自動補「上移 / 下移」鈕（.data-table__reorder-btn），卡片化 / 鍵盤可操作
 *   - reorder 模式自動停用分頁（避免拖到別頁），filter 仍可用
 *   - 每次成功重排 dispatch CustomEvent('admin-table:reorder', { detail:{ order:[id...] } })
 *     供業務頁接 backend；本元件不打 API。
 *   - reduced-motion：無位移 transition（CSS 端負責），JS 只做即時 DOM 重排
 *
 * 程式式 API：
 *   import { AdminTable } from './components/admin-table.js';
 *   const t = AdminTable.get('#consult-table');
 *   t.setFilter('pending'); t.goToPage(2);
 *   t.getOrder();           // reorder 模式下回傳目前 data-row-id 順序陣列
 */

import { Modal } from './modal.js'; // P0-9：停用 switch 攔截後開確認 dialog（共用 modal primitive）
// 分頁省略號 / item 樣板全站共用核心（前後台同源；視覺沿用 pagination.css atom 不重做）。
import { buildPaginationItems, renderItemButton, renderCompact } from './pagination.js';
// XSS 防護：data 字串進 template 前 escape（共用 util，對齊 render-cards.js；下方 AdminTable.escapeHtml 仍對外暴露同一份）
import { escapeHtml } from '../utils/dom.js';

// 手機 compact 斷點（≤640px）：與 uiux 規格一致。matchMedia 物件全表共用一份（同 query）。
const COMPACT_MQ = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(max-width: 640px)')
  : null;

const instances = new WeakMap(); // table element -> AdminTableInstance

/** 取排序比較值：優先 data-sort-value，否則 cell 文字 */
function cellSortValue(row, colIndex, type) {
  const cell = row.children[colIndex];
  if (!cell) return type === 'number' ? 0 : '';
  const raw = cell.getAttribute('data-sort-value');
  const val = raw !== null ? raw : cell.textContent.trim();
  if (type === 'number') return parseFloat(val.replace(/[^\d.-]/g, '')) || 0;
  if (type === 'date') return Date.parse(val) || 0;
  return val;
}

class AdminTableInstance {
  constructor(el) {
    this.el = el;
    this.tbody = el.querySelector('tbody');
    this.allRows = this.tbody ? Array.from(this.tbody.querySelectorAll('tr')) : [];
    // 附屬列（.data-table__row--expand，如 articles 退回意見）綁定母列：排序時跟隨母列移動，
    // 不參與欄排序比較（其僅含 colspan cell，無可比值）。母列由既有關聯解析，不改頁面 markup。
    this._indexExpandRows();
    // reorder 模式：拖曳排序時關閉分頁（拖到別頁無意義），其餘正常
    this.reorderMode = el.hasAttribute('data-admin-table-reorder');
    this.pageSize = this.reorderMode ? 0 : (parseInt(el.getAttribute('data-page-size'), 10) || 0); // 0 = 不分頁
    this.currentPage = 1;
    this.filterValue = 'all';
    this.searchQuery = '';   // P1-3：列表搜尋字串（與 tab filter 疊加；空字串＝不過濾）
    this.sortColIndex = -1;
    this.sortDir = 'none';
    this.drag = null; // 拖曳暫存
    this._searchTextCache = new WeakMap(); // primary tr -> 已正規化的可搜尋文字（建一次重用）

    this.headers = Array.from(el.querySelectorAll('thead th'));
    this.sortableHeaders = this.headers.filter((th) =>
      th.classList.contains('data-table__header--sortable') || th.hasAttribute('data-sort-key')
    );

    // 宣告式選用鉤子（categories 用；其餘表無對應屬性即 no-op，零回歸）：
    //   data-live-region="#id"       → SR 即時播報目標（P0-6）
    //   data-reorder-toggle="#id"    → 「調整排序 / 完成排序」切換鈕（P0-10）
    //   data-deactivate-modal="#id"  → 停用 switch 攔截後開的確認 modal（P0-9）
    this.liveRegion = this._resolveRef('data-live-region');
    this._reorderActive = false; // 排序模式開關狀態（toggle 控；初始收）

    // 外部空態區塊（宣告式，opt-in；沿用 doctors.html 的「兩段 empty-state」約定）：
    //   data-empty-zero="#id"      → 零資料空態（total === 0）
    //   data-empty-no-result="#id" → 篩選/搜尋無結果空態（total > 0 && visible === 0）
    // 兩者皆以原生 [hidden] 切顯隱（鏡像 consults / license 等頁既有 <div hidden> 慣例）。
    // 任一存在 → 由 _render 接管切換，且抑制注入式 _emptyStateRow（避免重複空態）。
    // 兩者皆不存在 → 退回既有注入列行為（零回歸，未遷移頁不受影響）。
    this.emptyZeroEl = this._resolveRef('data-empty-zero');
    this.emptyNoResultEl = this._resolveRef('data-empty-no-result');
    this.hasExternalEmpty = !!(this.emptyZeroEl || this.emptyNoResultEl);
    // 表格 wrapper（零資料時連同表格一併隱藏）。
    this.tableWrapper = this.el.closest('.data-table-wrapper');
    this._bindEmptyClear();

    // reorder 與 column-sort 互斥：拖曳重排時不啟用點欄排序（語意衝突）
    if (!this.reorderMode) this._bindSort();
    this._bindFilter();   // 內含 P0-1 載入同步 active tab + P1-1 count 同步
    this._bindSearch();   // P1-3：列表搜尋（input debounce 200ms，與 filter 疊加）
    this._bindPager();
    if (this.reorderMode) this._bindReorder();
    this._bindReorderToggle();   // P0-10：排序模式切換鈕（無 data-reorder-toggle 即 no-op）
    this._bindDeactivateGuard(); // P0-9：停用 switch 攔截 + 確認 modal（無 data-deactivate-modal 即 no-op）
    // P0-2：依 thead 既有 aria-sort 宣告做一次預設排序，讓顯示順序與宣告一致（reorder 模式不排序）。
    if (!this.reorderMode) this._initSortFromAria();
    this._render();
  }

  /** 解析「指向另一元素」的宣告式屬性（值為 CSS selector），找不到回 null。 */
  _resolveRef(attr) {
    const sel = this.el.getAttribute(attr);
    return sel ? document.querySelector(sel) : null;
  }

  /** P0-6：寫入 aria-live 區播報。先清空再寫，確保同字串重播也觸發 SR。 */
  _announce(msg) {
    if (!this.liveRegion || !msg) return;
    this.liveRegion.textContent = '';
    // 下一幀寫入：清空與賦值同步進行時部分 SR 不重讀，拆兩拍確保觸發。
    requestAnimationFrame(() => { this.liveRegion.textContent = msg; });
  }

  /**
   * 解析附屬列 → 母列關聯。
   * 附屬列 = tr.data-table__row--expand。母列解析優先序：
   *   1) 附屬列內任一後代的 aria-describedby 指向的元素，往上找最近的 tr（builder 既有關聯，
   *      如 articles 的 <div aria-describedby="art-title-4"> → #art-title-4 所在母列）；
   *   2) fallback：DOM 上最近的「前一個非附屬列」（附屬列緊貼母列後的版面約定）。
   * primaryRows = 排序 / 重排運作的單位（不含附屬列）；childMap：母列 → [附屬列…]。
   */
  _indexExpandRows() {
    this.childMap = new Map();       // primary tr -> [expand tr...]
    this.childToParent = new Map();  // expand tr -> primary tr
    this.primaryRows = [];
    if (!this.allRows.length) return;
    const isExpand = (r) => r.classList.contains('data-table__row--expand');
    this.allRows.forEach((row, i) => {
      if (!isExpand(row)) { this.primaryRows.push(row); return; }
      let parent = null;
      const ref = row.querySelector('[aria-describedby]');
      if (ref) {
        const described = document.getElementById(ref.getAttribute('aria-describedby'));
        const ownerRow = described ? described.closest('tr') : null;
        if (ownerRow && ownerRow !== row) parent = ownerRow;
      }
      if (!parent) {
        for (let j = i - 1; j >= 0; j -= 1) {
          if (!isExpand(this.allRows[j])) { parent = this.allRows[j]; break; }
        }
      }
      if (!parent) { this.primaryRows.push(row); return; } // 無母列可依附 → 視為獨立列
      const list = this.childMap.get(parent) || [];
      list.push(row);
      this.childMap.set(parent, list);
      this.childToParent.set(row, parent);
    });
    this.hasExpandRows = this.childMap.size > 0;
  }

  /** 將母列序列攤平回「母列 + 其附屬列」交錯順序（無附屬列時原樣回傳）。 */
  _withExpands(primaryRows) {
    if (!this.hasExpandRows) return primaryRows;
    const out = [];
    primaryRows.forEach((row) => {
      out.push(row);
      const kids = this.childMap.get(row);
      if (kids) out.push(...kids);
    });
    return out;
  }

  _bindSort() {
    this.sortableHeaders.forEach((th) => {
      th.setAttribute('role', 'columnheader');
      if (!th.hasAttribute('aria-sort')) th.setAttribute('aria-sort', 'none');
      th.tabIndex = 0;
      const colIndex = this.headers.indexOf(th);
      const handler = () => this._toggleSort(colIndex, th);
      th.addEventListener('click', handler);
      th.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handler();
        }
      });
    });
  }

  _toggleSort(colIndex, th) {
    const type = th.getAttribute('data-sort-type') || 'text';
    // 同欄循環 none → asc → desc → none
    if (this.sortColIndex === colIndex) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : (this.sortDir === 'desc' ? 'none' : 'asc');
    } else {
      this.sortColIndex = colIndex;
      this.sortDir = 'asc';
    }
    // 重置其他欄 aria-sort
    this.sortableHeaders.forEach((h) => h.setAttribute('aria-sort', 'none'));
    if (this.sortDir !== 'none') {
      th.setAttribute('aria-sort', this.sortDir === 'asc' ? 'ascending' : 'descending');
    } else {
      this.sortColIndex = -1;
    }

    this._applySort(colIndex, type, this.sortDir);
    this.currentPage = 1;
    this._render();
  }

  /**
   * 依指定欄 / 型別 / 方向重排 allRows（不碰 aria-sort、不 _render——由呼叫端負責）。
   * 供 _toggleSort（點擊循環）與 _initSortFromAria（載入預設）共用，確保兩路徑排序行為一致。
   * 只排序母列（primaryRows）；附屬列不參與比較，排序後再以 _withExpands 跟回母列下方。
   * dir === 'none' 時回到原始母列順序（this.primaryRows 保持原始 DOM 序、不就地改動）。
   */
  _applySort(colIndex, type, dir) {
    const ordered = this.primaryRows.slice();
    if (dir !== 'none') {
      const dirMul = dir === 'asc' ? 1 : -1;
      ordered.sort((a, b) => {
        const va = cellSortValue(a, colIndex, type);
        const vb = cellSortValue(b, colIndex, type);
        if (va < vb) return -1 * dirMul;
        if (va > vb) return 1 * dirMul;
        return 0;
      });
    }
    this.allRows = this._withExpands(ordered);
  }

  /**
   * P0-2：載入時讓「顯示順序」與 thead 既有 aria-sort 宣告一致。
   * 多頁硬寫 aria-sort="descending" 但 tbody 仍原始序（謊報）；init 觸發一次對應方向排序修正。
   * 找第一個 aria-sort 非 none 的可排序 th，執行 _applySort（沿用既有排序邏輯，不改 aria-sort）。
   * reorder 模式不啟用欄排序，故由呼叫端（constructor）排除，本法只在 !reorderMode 被呼叫。
   */
  _initSortFromAria() {
    const th = this.sortableHeaders.find((h) => {
      const s = h.getAttribute('aria-sort');
      return s === 'ascending' || s === 'descending';
    });
    if (!th) return;
    const colIndex = this.headers.indexOf(th);
    if (colIndex < 0) return;
    const type = th.getAttribute('data-sort-type') || 'text';
    this.sortColIndex = colIndex;
    this.sortDir = th.getAttribute('aria-sort') === 'ascending' ? 'asc' : 'desc';
    this._applySort(colIndex, type, this.sortDir);
  }

  _bindFilter() {
    const id = this.el.id ? `#${this.el.id}` : null;
    if (!id) return;
    const filterEl = document.querySelector(`[data-admin-table-filter="${id}"]`);
    if (!filterEl) return;
    this.filterEl = filterEl;

    // P0-1：載入時把初始 filterValue 對齊 HTML 既有 active tab（無 active 才退 'all'）。
    // 之前固定 'all' → 視覺停在 active tab 但資料顯全部；改讀 .is-active 的 data-filter-value 修正。
    const activeBtn = filterEl.querySelector('[data-filter-value].is-active');
    if (activeBtn) this.filterValue = activeBtn.getAttribute('data-filter-value') || 'all';

    // P1-1：依各列真實 data-status 計數回填 tabs__count，取代寫死數字。
    // TODO(backend): 列資料改 fetch 審核/管理列表 API 渲染（取代靜態 tr），資料量大改 server 分頁 — 見 docs/BACKEND_HANDOFF.md#admin-table-lists
    this._syncCounts(filterEl);

    filterEl.querySelectorAll('[data-filter-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        filterEl.querySelectorAll('[data-filter-value]').forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        this.setFilter(btn.getAttribute('data-filter-value'));
      });
    });
  }

  /**
   * P1-1：依真實列數回填各 tab 的 .tabs__count，取代頁面寫死數字。
   * 計數對象 = primaryRows（排除 .data-table__row--expand 附屬列，附屬列非獨立資料項，
   * 且其 data-status 與母列相同會造成重複計數）。'all' 取總母列數，其餘按 data-status 比對。
   */
  _syncCounts(filterEl) {
    if (!this.primaryRows.length) return;
    const total = this.primaryRows.length;
    const byStatus = new Map();
    this.primaryRows.forEach((r) => {
      const s = r.getAttribute('data-status');
      if (s) byStatus.set(s, (byStatus.get(s) || 0) + 1);
    });
    filterEl.querySelectorAll('[data-filter-value]').forEach((btn) => {
      const countEl = btn.querySelector('.tabs__count');
      if (!countEl) return;
      const v = btn.getAttribute('data-filter-value');
      countEl.textContent = v === 'all' ? total : (byStatus.get(v) || 0);
    });
  }

  /**
   * P1-3：列表搜尋。輸入框以 [data-admin-table-search="#tableId"] 關聯本表（鏡像 filter 的
   * data-admin-table-filter 慣例）。input 事件 debounce 200ms → setSearch；與 tab filter 疊加
   * （兩者皆收斂進 _visibleRows）。無對應輸入框則 no-op（搜尋為 opt-in，不影響 categories 等無搜尋表）。
   */
  _bindSearch() {
    const id = this.el.id ? `#${this.el.id}` : null;
    if (!id) return;
    const input = document.querySelector(`[data-admin-table-search="${id}"]`);
    if (!input) return;
    this.searchInput = input;
    let timer = null;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.setSearch(input.value), 200);
    });
  }

  /**
   * 取某母列的可搜尋文字（正規化：小寫 + 壓白）。
   * 比對範圍＝母列各 td 文字，但**排除操作欄**（.data-table__cell--actions，內含 編輯/刪除 等
   * 鈕字，否則打「編輯」會中所有列）與拖曳手把欄（.data-table__cell--handle）。
   * 若母列有附屬列（如 articles 退回意見），其文字一併納入 → 搜審查意見也能命中母列。
   * 每列算一次後存進 WeakMap 重用（避免每次 keystroke 重掃 DOM）。
   */
  _rowSearchText(primaryRow) {
    const cached = this._searchTextCache.get(primaryRow);
    if (cached !== undefined) return cached;
    const parts = [];
    primaryRow.querySelectorAll('td').forEach((td) => {
      if (td.classList.contains('data-table__cell--actions')) return;
      if (td.classList.contains('data-table__cell--handle')) return;
      parts.push(td.textContent);
    });
    // 納入附屬列文字（母列匹配範圍延伸到其退回意見等補充內容）
    const kids = this.childMap.get(primaryRow);
    if (kids) kids.forEach((kid) => parts.push(kid.textContent));
    const text = parts.join(' ').toLowerCase().replace(/\s+/g, ' ').trim();
    this._searchTextCache.set(primaryRow, text);
    return text;
  }

  _bindPager() {
    if (!this.pageSize) return;
    const id = this.el.id ? `#${this.el.id}` : null;
    // pager 可放 table 外（aria-controls）或 wrapper 內
    let pager = id ? document.querySelector(`[data-admin-table-pager="${id}"]`) : null;
    if (!pager) {
      const wrapper = this.el.closest('.data-table-wrapper') || this.el.parentNode;
      pager = wrapper ? wrapper.parentNode && wrapper.parentNode.querySelector('[data-admin-table-pager]') : null;
    }
    this.pager = pager || null;
  }

  /* ===== 排序模式拖曳重排 ===== */

  _bindReorder() {
    this.allRows.forEach((row) => this._prepRow(row));
    // 拖曳交給單一委派 handler（pointer 事件，滑鼠 + 觸控通用）
    this._onPointerDown = (e) => this._dragStart(e);
    this._onPointerMove = (e) => this._dragMove(e);
    this._onPointerUp = (e) => this._dragEnd(e);
    this.tbody.addEventListener('pointerdown', this._onPointerDown);
  }

  /* ===== P0-10：排序模式切換鈕 ===== */

  /**
   * 「調整排序 ↔ 完成排序」toggle（鈕以 data-reorder-toggle="#id" 宣告，無則 no-op）。
   * 開：table[data-reorder]=on（顯手把欄）+ 鈕 aria-pressed=true + 文案「完成排序」+
   *     button--secondary（teal 實心標示進行中）+ 各列啟用 switch disabled（排序時不可改狀態）。
   * 關：全數復原。狀態存 this._reorderActive。
   */
  _bindReorderToggle() {
    const btn = this._resolveRef('data-reorder-toggle');
    if (!btn) return;
    this._reorderToggleBtn = btn;
    this._reorderToggleLabel = { off: btn.textContent.trim() || '調整排序', on: '完成排序' };
    btn.addEventListener('click', () => this._setReorderActive(!this._reorderActive));
  }

  _setReorderActive(active) {
    if (active === this._reorderActive) return;
    this._reorderActive = active;
    this.el.setAttribute('data-reorder', active ? 'on' : 'off');

    const btn = this._reorderToggleBtn;
    if (btn) {
      btn.setAttribute('aria-pressed', String(active));
      btn.textContent = active ? this._reorderToggleLabel.on : this._reorderToggleLabel.off;
      // outline（待辦態）↔ secondary（teal 實心，進行中）切換
      btn.classList.toggle('button--outline', !active);
      btn.classList.toggle('button--secondary', active);
    }

    // 排序模式時各列啟用 switch disabled（避免拖曳 / 移動間誤改狀態）
    this._setRowSwitchesDisabled(active);

    this._announce(active ? '已進入排序模式，可拖曳或用上下移鈕調整順序' : '已完成排序');
  }

  /** 把 tbody 內所有 .form-switch__input 的 disabled 設為 on/off（排序模式鎖狀態切換）。 */
  _setRowSwitchesDisabled(disabled) {
    if (!this.tbody) return;
    this.tbody.querySelectorAll('.form-switch__input').forEach((input) => {
      input.disabled = disabled;
    });
  }

  /* ===== P0-9：停用 switch 攔截 + 確認 modal ===== */

  /**
   * 啟用 / 停用 switch 監聽（modal 以 data-deactivate-modal="#id" 宣告，無則 no-op）。
   * 停用（switch 轉 off）：先把視覺彈回 on（停用未生效），開確認 modal；
   *   確認 → 真的關 + 翻 status/pill + 播報；取消 / ESC / backdrop → 維持 on（已彈回）。
   * 啟用（switch 轉 on）：無破壞性，直接生效 + 翻 status/pill + 播報。
   * 用 change 委派（鍵盤 space + 滑鼠點擊皆觸發），避免 click/keydown 雙路徑分歧。
   */
  /**
   * 把 role="switch" 的 aria-checked 同步到 input.checked。
   * checkbox 設了顯式 aria-checked 後不會隨 .checked 自動更新，每次程式改 .checked 都得補一拍，
   * 否則 SR 讀到落後狀態（鏡像 platform-products-edit 的 checked + aria-checked 宣告契約）。
   */
  _syncSwitchAria(input) {
    if (input && input.getAttribute('role') === 'switch') {
      input.setAttribute('aria-checked', String(input.checked));
    }
  }

  _bindDeactivateGuard() {
    const modalSel = this.el.getAttribute('data-deactivate-modal');
    if (!modalSel || !this.tbody) return;
    this._deactivateModalSel = modalSel;
    this.tbody.addEventListener('change', (e) => {
      const input = e.target.closest('.form-switch__input');
      if (!input || !this.tbody.contains(input)) return;
      const row = input.closest('tr');
      if (!row) return;
      if (input.checked) {
        this._syncSwitchAria(input);      // SR 同步：啟用態
        this._applyRowStatus(row, true);  // 啟用：直接生效
      } else {
        input.checked = true;             // 先彈回（停用待確認）
        this._syncSwitchAria(input);      // SR 同步：彈回啟用態
        this._confirmDeactivate(row, input);
      }
    });
  }

  /**
   * 開停用確認 modal：帶入分類名 / 文章數，confirm → 真停用，dismiss → 維持啟用。
   * close 監聽只註冊一次（modal primitive 的 on() 無 off()，重複 on 會累積），
   * 改以 this._pendingDeactivate 暫存當前列／input／是否確認，close 時讀取後清空。
   */
  _confirmDeactivate(row, input) {
    const modal = Modal.get(this._deactivateModalSel);
    if (!modal) { input.checked = false; this._syncSwitchAria(input); this._applyRowStatus(row, false); return; } // 無 modal 退化為直接停用

    const name = this._rowLabel(row);
    const countCell = row.querySelector('.data-table__cell--num');
    const count = countCell ? countCell.textContent.trim() : '';
    this._fillDeactivateModal(modal.el, name, count);

    this._pendingDeactivate = { row, input, confirmed: false };

    // 一次性接線（confirm 鈕 click / modal close）—— 用 dataset 旗標保證只綁一次。
    if (!modal.el.dataset.deactivateWired) {
      modal.el.dataset.deactivateWired = '1';
      const confirmBtn = modal.el.querySelector('[data-deactivate-confirm]')
        || modal.el.querySelector('#deactivate-category-confirm');
      if (confirmBtn) {
        confirmBtn.addEventListener('click', () => {
          if (this._pendingDeactivate) this._pendingDeactivate.confirmed = true;
          modal.close();
        });
      }
      modal.on('close', () => {
        const p = this._pendingDeactivate;
        this._pendingDeactivate = null;
        if (!p) return;
        if (p.confirmed) {
          p.input.checked = false;
          this._syncSwitchAria(p.input);       // SR 同步：停用態
          this._applyRowStatus(p.row, false); // 確認 → 真停用
        } else {
          p.input.checked = true;             // 取消 / ESC / backdrop → 維持啟用
          this._syncSwitchAria(p.input);       // SR 同步：維持啟用態
          p.input.focus();
        }
      });
    }
    modal.open();
  }

  /** 把分類名 / 文章數塞進 modal 對應 slot（宣告式 data-* 優先，退回 categories 既有 id）。 */
  _fillDeactivateModal(modalEl, name, count) {
    const nameEl = modalEl.querySelector('[data-deactivate-name]') || modalEl.querySelector('#deactivate-category-name');
    const countEl = modalEl.querySelector('[data-deactivate-count]') || modalEl.querySelector('#deactivate-category-count');
    if (nameEl) nameEl.textContent = name;
    if (countEl && count) countEl.textContent = count;
  }

  /**
   * 翻轉某列啟用狀態的衍生 UI：data-status + 狀態 pill 文字/樣式 + SR 播報。
   * 純前台視覺（MVP；後端串接另派）。pill 以 .admin-pill--active / --filed 兩態切換（categories 既有）。
   */
  _applyRowStatus(row, active) {
    row.setAttribute('data-status', active ? 'active' : 'inactive');
    const pill = row.querySelector('.admin-pill');
    if (pill) {
      pill.classList.toggle('admin-pill--active', active);
      pill.classList.toggle('admin-pill--filed', !active);
      pill.textContent = active ? '啟用中' : '已停用';
    }
    this._announce(`${this._rowLabel(row)} 已${active ? '啟用' : '停用'}`);
  }

  /** 每列補：grip 可拖 + 上移/下移鈕（卡片化 / 鍵盤降級）。冪等。 */
  _prepRow(row) {
    if (row.dataset.reorderReady === '1') return;
    row.dataset.reorderReady = '1';

    const grip = row.querySelector('.data-table__reorder-grip');
    if (grip) {
      grip.style.touchAction = 'none'; // 防觸控捲動搶走 pointer 序列
      grip.setAttribute('role', 'button');
      grip.setAttribute('aria-hidden', 'false');
      grip.setAttribute('aria-label', '拖曳重新排序');
    }

    // 上移 / 下移鈕：注入 grip 所在 cell（無 grip 則第一個 cell）。
    // P0-8：鈕對包進 .data-table__reorder 容器 — 該 class 是 admin-data.css 既有的卡片化收展鉤子
    // （桌機 display:none、卡片態 [data-reorder="on"] 顯示）。先前鈕直接掛 td 致 CSS 完全不套用。
    const host = grip ? grip.closest('td') : row.querySelector('td');
    if (!host || host.querySelector('.data-table__reorder')) return;
    // 沿用既有 icon 鈕系統（.button--icon = 44×44 觸控達標、.button--ghost = navy ghost + hover/focus），
    // 零新 CSS。不加 .button--sm（那是 36×36，低於 44 觸控底線；上下移鈕是手機主路徑）。
    // .data-table__reorder-btn[--dir] 保留作 JS 鉤子 / 委派選取。
    const mkBtn = (dir, label, glyph) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `button button--icon button--ghost data-table__reorder-btn data-table__reorder-btn--${dir}`;
      b.dataset.reorderDir = dir;
      b.setAttribute('aria-label', label);
      b.innerHTML = `<span aria-hidden="true">${glyph}</span>`;
      return b;
    };
    const group = document.createElement('div');
    group.className = 'data-table__reorder';
    group.appendChild(mkBtn('up', '上移一列', '↑'));
    group.appendChild(mkBtn('down', '下移一列', '↓'));
    host.appendChild(group);
    // 按鈕委派一次綁在 tbody（_bindReorder 後才有鈕，故這裡 ensure 綁定）
    if (!this.tbody.dataset.reorderBtnBound) {
      this.tbody.dataset.reorderBtnBound = '1';
      this.tbody.addEventListener('click', (e) => {
        const btn = e.target.closest('.data-table__reorder-btn');
        if (!btn || !this.tbody.contains(btn)) return;
        const r = btn.closest('tr');
        if (r) this._moveByStep(r, btn.dataset.reorderDir === 'up' ? -1 : 1);
      });
    }
  }

  _dragStart(e) {
    const grip = e.target.closest('.data-table__reorder-grip');
    if (!grip) return; // 只有抓 grip 才啟動拖曳（避免誤觸 / 與按鈕衝突）
    const row = grip.closest('tr');
    if (!row) return;
    e.preventDefault();
    this.drag = { row, startOrder: this.getOrder() };
    row.classList.add('is-dragging');
    // 在 grip 上捕捉指標，move/up 期間穩定收事件（含指標移出列外）
    try { grip.setPointerCapture(e.pointerId); } catch (_) { /* 老瀏覽器忽略 */ }
    this._dragGrip = grip;
    grip.addEventListener('pointermove', this._onPointerMove);
    grip.addEventListener('pointerup', this._onPointerUp);
    grip.addEventListener('pointercancel', this._onPointerUp);
  }

  _dragMove(e) {
    if (!this.drag) return;
    const dragging = this.drag.row;
    // 找指標 Y 落在哪一列：取「目前可見」列（reorder 模式 pageSize=0，故 = 全列扣 filter）
    const rows = this._visibleRows().filter((r) => r !== dragging);
    let target = null;
    for (const r of rows) {
      const rect = r.getBoundingClientRect();
      if (e.clientY < rect.top + rect.height / 2) { target = r; break; }
    }
    if (target) {
      // 插到 target 前；DOM 已是該位置則跳過（避免無謂 reflow）
      if (dragging.nextElementSibling !== target) this.tbody.insertBefore(dragging, target);
      // P0-7：落點線顯示於 target 上緣（.is-drop-target，CSS inset top 2px teal）
      this._setDropTarget(target, 'above');
    } else {
      // 落在所有列下方 → 移到尾端
      if (this.tbody.lastElementChild !== dragging) this.tbody.appendChild(dragging);
      // P0-7：落在最後 → 落點線顯示於末列下緣（.is-drop-target--below）
      this._setDropTarget(dragging.previousElementSibling, 'below');
    }
  }

  /**
   * P0-7：在「即將插入位置」的列畫落點線。一次只亮一條（先清舊再設新）。
   * pos==='above' → .is-drop-target（CSS inset top 2px）；'below' → .is-drop-target--below（inset bottom）。
   * 拖曳列自身不畫（避免線落在 ghost 上）；reduced-motion 不影響（box-shadow 非動畫）。
   */
  _setDropTarget(row, pos) {
    if (row === this._dropTarget && pos === this._dropPos) return; // 同位置不重設
    this._clearDropTarget();
    if (!row || row === (this.drag && this.drag.row)) return;
    row.classList.add(pos === 'below' ? 'is-drop-target--below' : 'is-drop-target');
    this._dropTarget = row;
    this._dropPos = pos;
  }

  _clearDropTarget() {
    if (this._dropTarget) {
      this._dropTarget.classList.remove('is-drop-target', 'is-drop-target--below');
    }
    this._dropTarget = null;
    this._dropPos = null;
  }

  _dragEnd() {
    if (!this.drag) return;
    const { row, startOrder } = this.drag;
    row.classList.remove('is-dragging');
    this._clearDropTarget(); // P0-7：放開即收落點線
    if (this._dragGrip) {
      this._dragGrip.removeEventListener('pointermove', this._onPointerMove);
      this._dragGrip.removeEventListener('pointerup', this._onPointerUp);
      this._dragGrip.removeEventListener('pointercancel', this._onPointerUp);
      this._dragGrip = null;
    }
    // 同步 allRows 內部順序 = 現在 DOM 順序
    this._syncOrderFromDom();
    this.drag = null;
    if (this.getOrder().join('|') !== startOrder.join('|')) {
      this._dispatchReorder();
      // P0-6：播報拖曳後新位置（第 N / 共 M 項）
      this._announceRowPosition(row);
    }
  }

  /** 上移/下移鈕：在「可見列」序列中與相鄰列互換（跨 filter 隱藏列無感） */
  _moveByStep(row, step) {
    const visible = this._visibleRows();
    const i = visible.indexOf(row);
    const j = i + step;
    if (i < 0 || j < 0 || j >= visible.length) return; // 邊界不動
    const ref = visible[j];
    const before = this.getOrder().join('|');
    if (step < 0) this.tbody.insertBefore(row, ref);
    else this.tbody.insertBefore(row, ref.nextElementSibling);
    this._syncOrderFromDom();
    if (this.getOrder().join('|') !== before) {
      this._dispatchReorder();
      this._announceRowPosition(row); // P0-6：播報新位置
      const movedBtn = row.querySelector(`.data-table__reorder-btn--${step < 0 ? 'up' : 'down'}`);
      if (movedBtn) movedBtn.focus(); // 鍵盤連續操作焦點不丟
    }
  }

  _syncOrderFromDom() {
    this.allRows = Array.from(this.tbody.querySelectorAll('tr'));
  }

  /** 取列顯示名（主名稱欄文字，缺則退 data-row-id），供 SR 播報用。 */
  _rowLabel(row) {
    const nameCell = row.querySelector('.data-table__cell--name');
    const name = nameCell ? nameCell.textContent.trim() : '';
    return name || row.getAttribute('data-row-id') || '項目';
  }

  /** P0-6：播報某列重排後的新位置（「X 已移至第 N 項，共 M 項」）。 */
  _announceRowPosition(row) {
    if (!this.liveRegion) return;
    const visible = this._visibleRows();
    const pos = visible.indexOf(row) + 1;
    if (pos < 1) return;
    this._announce(`${this._rowLabel(row)} 已移至第 ${pos} 項，共 ${visible.length} 項`);
  }

  /** reorder 模式：目前 data-row-id 順序（無 id 的列以 index fallback） */
  getOrder() {
    return Array.from(this.tbody ? this.tbody.querySelectorAll('tr') : [])
      .map((r, i) => r.getAttribute('data-row-id') || `__idx-${i}`);
  }

  _dispatchReorder() {
    this.el.dispatchEvent(new CustomEvent('admin-table:reorder', {
      bubbles: true,
      detail: { order: this.getOrder() },
    }));
  }

  /**
   * 可見列＝同時通過「tab 篩選」與「搜尋」的母列 + 其附屬列（疊加，P1-3）。
   * 兩條件都對「母列」判定：tab 看 data-status、搜尋看 _rowSearchText；附屬列可見性
   * 一律跟隨母列（不單獨判定），保持母子不離。回傳維持 this.allRows 的順序（含排序結果）。
   * 快路徑：無 filter（all）且無搜尋 → 直接回 allRows（零回歸，與原行為一致）。
   */
  _visibleRows() {
    const filtering = this.filterValue !== 'all';
    const q = this.searchQuery;
    const searching = q.length > 0;
    if (!filtering && !searching) return this.allRows;

    const shownParents = new Set(
      this.primaryRows.filter((r) => {
        if (filtering && r.getAttribute('data-status') !== this.filterValue) return false;
        if (searching && !this._rowSearchText(r).includes(q)) return false;
        return true;
      })
    );
    return this.allRows.filter((r) => {
      const parent = this.childToParent.get(r); // r 是附屬列 → 跟隨母列；否則看自身
      return shownParents.has(parent || r);
    });
  }

  /**
   * P0-3：篩選後可見列數=0 時，動態注入一列空態（沿用 admin.css 既有 .empty-state 結構，
   * 塞進 colspan 滿欄的 td.data-table__empty——與業務頁靜態空態同一視覺，不另造樣式）。
   * 冪等：節點建一次後快取重用，render 時依需求 attach / detach。
   * 文案為功能性占位「沒有符合篩選的項目」（待 rone / wireframe 確認）。
   */
  _emptyStateRow() {
    if (this._emptyRow) return this._emptyRow;
    const colSpan = this.headers.length || 1;
    const tr = document.createElement('tr');
    tr.className = 'data-table__row--empty';
    tr.setAttribute('data-admin-table-empty', '');
    const td = document.createElement('td');
    td.className = 'data-table__empty';
    td.colSpan = colSpan;
    td.innerHTML = '<div class="empty-state"><p class="empty-state__title">沒有符合篩選的項目</p></div>';
    tr.appendChild(td);
    this._emptyRow = tr;
    return tr;
  }

  /**
   * 「清除篩選/搜尋」鈕（多在 no-result 空態內，以 data-admin-table-clear 宣告，無則 no-op）。
   * 鈕可放表格外任意處；以委派一次綁在 document，比對 data-admin-table-clear 值 == 本表 id。
   * 點擊 → 清搜尋框 + 篩選回「全部」（同步 tab .is-active / aria-pressed）+ 重 render。
   */
  _bindEmptyClear() {
    const id = this.el.id ? `#${this.el.id}` : null;
    if (!id) return;
    const clearBtns = document.querySelectorAll(`[data-admin-table-clear="${id}"]`);
    if (!clearBtns.length) return;
    clearBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.clearFilters());
    });
  }

  /** 清除搜尋 + 篩選回「全部」並重 render（供 no-result 空態「清除」鈕用）。 */
  clearFilters() {
    if (this.searchInput) this.searchInput.value = '';
    this.searchQuery = '';
    // 篩選回 all：同步 tab UI（鏡像 _bindFilter 點擊邏輯）
    if (this.filterEl) {
      this.filterEl.querySelectorAll('[data-filter-value]').forEach((b) => {
        const isAll = b.getAttribute('data-filter-value') === 'all';
        b.classList.toggle('is-active', isAll);
        b.setAttribute('aria-pressed', String(isAll));
      });
    }
    this.filterValue = 'all';
    this.currentPage = 1;
    this._render();
    return this;
  }

  /**
   * 外部兩段空態切換（hasExternalEmpty 才作用）。依 total / visible 三態：
   *   total === 0           → 顯零資料、隱 no-result、隱表格 wrapper。
   *   total>0 && visible===0 → 顯 no-result、隱零資料、隱表格 wrapper。
   *   visible > 0           → 兩空態皆隱、表格 wrapper 顯示。
   * total = 母列數（primaryRows，排除附屬列）；visible 由呼叫端傳入（_visibleRows 內母列數）。
   * 缺哪一段就跳過哪一段（如僅宣告零資料、未宣告 no-result）。
   */
  _syncExternalEmpty(visibleCount) {
    if (!this.hasExternalEmpty) return;
    const total = this.primaryRows.length;
    const showZero = total === 0;
    const showNoResult = total > 0 && visibleCount === 0;
    const hideTable = showZero || showNoResult;
    if (this.emptyZeroEl) this.emptyZeroEl.hidden = !showZero;
    if (this.emptyNoResultEl) this.emptyNoResultEl.hidden = !showNoResult;
    if (this.tableWrapper) this.tableWrapper.hidden = hideTable;
  }

  _render() {
    if (!this.tbody) return;
    const rows = this._visibleRows();
    // 外部空態以「母列」總數 / 可見數判定（與 tab count 同基準，排除附屬列）。
    const visiblePrimaryCount = this.hasExternalEmpty
      ? rows.filter((r) => !this.childToParent.has(r)).length
      : rows.length;

    // 先全部 detach，再依分頁 append（批次操作，避免逐列 reflow）
    const frag = document.createDocumentFragment();
    let pageRows = rows;
    if (this.pageSize > 0) {
      const start = (this.currentPage - 1) * this.pageSize;
      pageRows = rows.slice(start, start + this.pageSize);
    }
    this.allRows.forEach((r) => { if (r.parentNode) r.parentNode.removeChild(r); });
    // 注入的空態列不在 allRows 內，需獨立 detach（避免殘留在有結果時）
    if (this._emptyRow && this._emptyRow.parentNode) this._emptyRow.parentNode.removeChild(this._emptyRow);

    if (pageRows.length === 0) {
      // 有外部空態區塊 → 由 _syncExternalEmpty 接管，不注入空態列（避免雙空態）。
      if (!this.hasExternalEmpty) frag.appendChild(this._emptyStateRow());
    } else {
      pageRows.forEach((r) => frag.appendChild(r));
    }
    this.tbody.appendChild(frag);

    this._syncExternalEmpty(visiblePrimaryCount);
    this._renderPager(rows.length);
  }

  /**
   * 渲染分頁列。改吃共用 buildPaginationItems（省略號規則全站一致），不再就地展開全頁碼。
   * 由 totalRows 算頁數後委由 _paintPager 上漆；頁數另存 _pageCount 供斷點切換時免重算重繪。
   */
  _renderPager(totalRows) {
    if (!this.pager || !this.pageSize) return;
    this._pageCount = Math.max(1, Math.ceil(totalRows / this.pageSize));
    this._bindPagerDelegation();
    this._bindPagerCompactWatch();
    this._paintPager();
  }

  /**
   * 依目前 currentPage / _pageCount / 斷點上漆 pager innerHTML。
   * compact（≤640px）：切 .pagination--compact 渲染 ‹ + cur/total + ›（uiux 規格 §4）；
   * standard：buildPaginationItems → button 樣板。pageCount<=1 清空（單頁不顯分頁）。
   * 純上漆、不重算頁數，故斷點 matchMedia change 可直接重呼叫（不必重跑整表 _render）。
   */
  _paintPager() {
    const pageCount = this._pageCount || 1;
    if (pageCount <= 1) {
      this.pager.innerHTML = '';
      this.pager.classList.remove('pagination--compact');
      return;
    }
    const cur = this.currentPage;
    const compact = !!(COMPACT_MQ && COMPACT_MQ.matches);
    this.pager.classList.toggle('pagination--compact', compact);
    this.pager.innerHTML = compact
      ? renderCompact(cur, pageCount, { mode: 'button' })
      : buildPaginationItems(cur, pageCount).map(renderItemButton).join('');
  }

  /** 單一委派接管所有 [data-page] 點擊（綁一次；innerHTML 重繪不影響委派）。 */
  _bindPagerDelegation() {
    if (this._pagerDelegated || !this.pager) return;
    this._pagerDelegated = true;
    this.pager.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-page]');
      if (!btn || !this.pager.contains(btn)) return;
      if (btn.getAttribute('aria-disabled') === 'true') return;
      const p = parseInt(btn.getAttribute('data-page'), 10);
      if (!Number.isNaN(p)) this.goToPage(p);
    });
  }

  /** 跨 standard↔compact 斷點時只重漆 pager（免重跑 _render）。matchMedia change 綁一次。 */
  _bindPagerCompactWatch() {
    if (this._compactWatched || !COMPACT_MQ) return;
    this._compactWatched = true;
    const onChange = () => this._paintPager();
    if (COMPACT_MQ.addEventListener) COMPACT_MQ.addEventListener('change', onChange);
    else if (COMPACT_MQ.addListener) COMPACT_MQ.addListener(onChange); // Safari < 14 降級
  }

  setFilter(value) {
    this.filterValue = value || 'all';
    this.currentPage = 1;
    this._render();
    return this;
  }

  /**
   * P1-3：設定搜尋字串並重繪。查詢字串以與 _rowSearchText 相同方式正規化（小寫 + 壓白），
   * 使 includes 比對一致。改查詢後回首頁（避免停在已不存在的分頁），_render 內若 0 筆會注入空態。
   */
  setSearch(value) {
    this.searchQuery = String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    this.currentPage = 1;
    this._render();
    return this;
  }

  goToPage(p) {
    this.currentPage = p;
    this._render();
    return this;
  }
}

function getOrCreate(target) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) return null;
  let inst = instances.get(el);
  if (!inst) {
    inst = new AdminTableInstance(el);
    instances.set(el, inst);
  }
  return inst;
}

export const AdminTable = {
  get(target) { return getOrCreate(target); },
  init(target) { return getOrCreate(target); },
  // 對外暴露 escape，供其他後台 renderer 共用
  escapeHtml,
};

// ===== Auto-init =====
function bindAll(root = document) {
  root.querySelectorAll('[data-admin-table]').forEach((el) => getOrCreate(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bindAll());
} else {
  bindAll();
}

if (typeof window !== 'undefined') {
  window.AdminTable = AdminTable;
}
